import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Target, Calendar, CheckCircle, Clock, Trophy,
  Loader2, ChevronRight, Flame, BookOpen, FileText,
  BarChart3, Zap, Star, Award, Lock, Crown
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const RoadmapPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Freemium check
  const isPro = user?.role === "PRO";
  
  const [loading, setLoading] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [myTrack, setMyTrack] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [consistency, setConsistency] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user, token, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch tracks
      const tracksRes = await fetch(`${API_URL}/api/roadmap/tracks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const tracksData = await tracksRes.json();
      setTracks(tracksData.tracks || []);

      // Fetch my track
      const myTrackRes = await fetch(`${API_URL}/api/roadmap/my-track`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const myTrackData = await myTrackRes.json();
      setMyTrack(myTrackData);

      // If has track, fetch milestones and consistency
      if (myTrackData.year_track) {
        const [milestonesRes, consistencyRes, challengesRes] = await Promise.all([
          fetch(`${API_URL}/api/roadmap/milestones`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/roadmap/consistency`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/roadmap/challenges`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        const milestonesData = await milestonesRes.json();
        setMilestones(milestonesData.milestones || []);

        const consistencyData = await consistencyRes.json();
        setConsistency(consistencyData);

        const challengesData = await challengesRes.json();
        setChallenges(challengesData.challenges || []);
      }
    } catch (error) {
      console.error('Error fetching roadmap data:', error);
      toast.error('Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  };

  const selectTrack = async (yearTrack) => {
    setSelecting(true);
    try {
      const res = await fetch(`${API_URL}/api/roadmap/select-track`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ year_track: yearTrack })
      });

      if (!res.ok) throw new Error('Failed to select track');

      toast.success(`Year ${yearTrack} track selected!`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error selecting track:', error);
      toast.error('Failed to select track');
    } finally {
      setSelecting(false);
    }
  };

  const updateMilestone = async (taskId, completed) => {
    // Freemium check - only PRO users can track progress
    if (!isPro) {
      toast.error('Upgrade to PRO to track your progress', {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/upgrade')
        }
      });
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/roadmap/milestones/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ milestone_id: taskId, completed })
      });

      // Update local state
      setMilestones(prev => prev.map(milestone => ({
        ...milestone,
        tasks: milestone.tasks.map(task => 
          task.id === taskId ? { ...task, completed } : task
        )
      })));

      toast.success(completed ? 'Task completed!' : 'Task unmarked');
    } catch (error) {
      console.error('Error updating milestone:', error);
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-500">Loading your roadmap...</p>
        </div>
      </div>
    );
  }

  // Track Selection View (if no track selected)
  if (!myTrack?.year_track) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              🗺️ Choose Your UPSC Journey
            </h1>
            <p className="text-slate-500 text-lg">
              Select your year track based on where you are in your preparation
            </p>
          </div>

          <div className="grid gap-6">
            {tracks.map((track) => (
              <Card 
                key={track.year}
                data-testid={`track-card-year-${track.year}`}
                className="bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => !selecting && selectTrack(track.year)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl flex-shrink-0"
                      style={{ backgroundColor: `${track.color}15` }}
                    >
                      {track.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge 
                          className="text-sm"
                          style={{ backgroundColor: track.color, color: 'white' }}
                        >
                          Year {track.year}
                        </Badge>
                        <h2 className="text-xl font-bold text-slate-900">{track.name}</h2>
                      </div>
                      <p className="text-slate-600 mb-3">{track.subtitle}</p>
                      <p className="text-slate-500 text-sm mb-4">{track.description}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {track.focus_areas.map((area, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs text-slate-500 border-slate-300">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button
                      data-testid={`select-track-btn-${track.year}`}
                      className="flex-shrink-0"
                      style={{ backgroundColor: track.color }}
                      disabled={selecting}
                    >
                      {selecting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Select'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main Roadmap View (has track selected)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Freemium Banner */}
        {!isPro && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">
                  View Mode Only • Upgrade to track your progress
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  PRO users can mark tasks complete and track streaks
                </p>
              </div>
              <Link to="/upgrade">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Crown className="h-4 w-4 mr-1" />
                  Upgrade
                </Button>
              </Link>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">{myTrack.track_info?.icon}</span>
              <h1 className="text-3xl font-bold text-slate-900">
                {myTrack.track_info?.name}
              </h1>
              <Badge 
                className="text-sm"
                style={{ backgroundColor: myTrack.track_info?.color }}
              >
                Year {myTrack.year_track}
              </Badge>
              {isPro ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  <Crown className="h-3 w-3 mr-1" />
                  PRO
                </Badge>
              ) : (
                <Badge variant="outline" className="border-amber-300 text-amber-600">
                  VIEW ONLY
                </Badge>
              )}
            </div>
            <p className="text-slate-500">{myTrack.track_info?.subtitle}</p>
          </div>
          <Link to="/roadmap/weekly">
            <Button data-testid="weekly-plan-btn" className="bg-emerald-500 hover:bg-emerald-600">
              <Calendar className="h-4 w-4 mr-2" />
              Weekly Plan
            </Button>
          </Link>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* Progress */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-xs sm:text-sm">Overall Progress</span>
                <Target className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                {myTrack.progress?.percentage || 0}%
              </p>
              <Progress value={myTrack.progress?.percentage || 0} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">
                {myTrack.progress?.completed_tasks || 0}/{myTrack.progress?.total_tasks || 0} tasks
              </p>
            </CardContent>
          </Card>

          {/* Consistency Score */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-xs sm:text-sm">Consistency</span>
                <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                {consistency?.consistency_score || 0}%
              </p>
              <Badge 
                className="mt-2 text-xs"
                style={{ 
                  backgroundColor: `${consistency?.status_color}20`,
                  color: consistency?.status_color
                }}
              >
                {consistency?.status_text || 'Loading...'}
              </Badge>
            </CardContent>
          </Card>

          {/* Current Streak */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-xs sm:text-sm">Current Streak</span>
                <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                {consistency?.current_streak || 0} days
              </p>
              <p className="text-xs text-slate-500 mt-2">Keep it going!</p>
            </CardContent>
          </Card>

          {/* Challenges */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-500 text-xs sm:text-sm">Challenges</span>
                <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                {challenges.filter(c => c.completed).length}/{challenges.length}
              </p>
              <p className="text-xs text-slate-500 mt-2">completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Milestones (Left 2 cols) */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-5 w-5 text-emerald-500" />
              Milestones
            </h2>
            
            {milestones.map((milestone, idx) => (
              <Card 
                key={idx}
                className={`bg-white border-slate-200 shadow-sm ${
                  milestone.is_complete ? 'border-emerald-300' : ''
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        milestone.is_complete 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {milestone.is_complete ? <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" /> : milestone.month}
                      </div>
                      <div>
                        <CardTitle className="text-base sm:text-lg text-slate-900">{milestone.title}</CardTitle>
                        <p className="text-slate-500 text-sm">Month {milestone.month}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-slate-500 border-slate-300">
                      {milestone.completed_tasks}/{milestone.total_tasks}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-500 text-sm mb-4">{milestone.description}</p>
                  
                  <div className="space-y-2">
                    {milestone.tasks.map((task, taskIdx) => (
                      <div 
                        key={taskIdx}
                        data-testid={`milestone-task-${task.id}`}
                        className={`flex items-center gap-3 p-2 sm:p-3 rounded-lg cursor-pointer transition-all ${
                          task.completed 
                            ? 'bg-emerald-50 border border-emerald-200' 
                            : 'bg-slate-50 hover:bg-white border border-slate-200'
                        }`}
                        onClick={() => updateMilestone(task.id, !task.completed)}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          task.completed 
                            ? 'bg-emerald-500 border-emerald-500' 
                            : 'border-slate-300'
                        }`}>
                          {task.completed && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                        <span className={`flex-1 text-sm sm:text-base ${task.completed ? 'text-emerald-600 line-through' : 'text-slate-700'}`}>
                          {task.title}
                        </span>
                        <Badge variant="outline" className="text-xs text-slate-500 border-slate-300 hidden sm:inline-flex">
                          {task.type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Sidebar (Right col) */}
          <div className="space-y-4 sm:space-y-6">
            {/* Focus Areas */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-cyan-500" />
                  Focus Areas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {myTrack.track_info?.focus_areas?.map((area, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-slate-600">
                    <ChevronRight className="h-4 w-4 text-emerald-500" />
                    {area}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Challenges */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-purple-500" />
                  Active Challenges
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {challenges.slice(0, 4).map((challenge, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${
                      challenge.completed 
                        ? 'bg-purple-50 border border-purple-200' 
                        : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl">{challenge.icon}</span>
                      <div className="flex-1">
                        <p className={`font-medium text-sm sm:text-base ${challenge.completed ? 'text-purple-600' : 'text-slate-900'}`}>
                          {challenge.title}
                        </p>
                        <p className="text-xs text-slate-500">{challenge.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={challenge.progress_percent} 
                        className="h-1.5 flex-1" 
                      />
                      <span className="text-xs text-slate-400">
                        {challenge.current}/{challenge.target}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-emerald-500/30">
              <CardContent className="p-4 space-y-3">
                <Link to="/foundation" className="block">
                  <Button variant="outline" className="w-full justify-start border-slate-600 hover:bg-emerald-500/20">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Continue NCERT
                  </Button>
                </Link>
                <Link to="/tests" className="block">
                  <Button variant="outline" className="w-full justify-start border-slate-600 hover:bg-emerald-500/20">
                    <FileText className="h-4 w-4 mr-2" />
                    Take a Test
                  </Button>
                </Link>
                <Link to="/daily-intelligence" className="block">
                  <Button variant="outline" className="w-full justify-start border-slate-600 hover:bg-emerald-500/20">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Daily News
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapPage;
